import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { THEME_STORAGE_KEY } from '@/config/env';

type ThemeMode = 'light' | 'dark';

interface AppContextValue {
  themeMode: ThemeMode;
  toggleTheme: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<AppContextValue | null>(null);

function readStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* fall through to the default */
  }

  // Light is the default; dark only when the user has picked it with the toggle.
  return 'light';
}

/**
 * Presentation state only.
 *
 * This used to hold `chatbotId` as well — the tenant every request was scoped
 * to, typed in by hand and kept in localStorage. Tenancy now comes from the
 * signed-in user's workspace memberships (`WorkspaceContext`), so what is left
 * here is genuinely just the theme.
 */
export function AppProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(readStoredTheme);

  const toggleTheme = useCallback(() => {
    setThemeMode((current) => {
      const next = current === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* not fatal */
      }
      return next;
    });
  }, []);

  // Jodit renders outside antd's styling, so the mode is mirrored onto the
  // document for the CSS overrides in global.css to hook into.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  const value = useMemo(
    () => ({ themeMode, toggleTheme }),
    [themeMode, toggleTheme]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
